/**
 * AdminDescriptions — 라벨/내용 목록 (테두리형, 상세보기용)
 *
 * Ant Design 의 Descriptions 와 유사. 상세 화면에서 "항목명 + 값"을 표 형태로 보여준다.
 * content 는 ReactNode 이므로 배지/버튼/링크 등 컴포넌트도 넣을 수 있다.
 *
 * 상호작용이 없어 서버 컴포넌트로도 사용 가능하다.
 *
 * @example
 * <AdminDescriptions
 *   title="회원 정보"
 *   columns={2}
 *   items={[
 *     { label: "이름", content: "홍길동" },
 *     { label: "상태", content: <Badge color="green" text="활성" /> },
 *     { label: "메모", content: "...", span: 2 }, // 한 줄 전체 차지
 *     // content 엘리먼트에 required prop 이 있으면(AdminFormField 등) 자동으로 * 가 붙는다.
 *     { label: "이메일", content: <AdminFormField {...form.field("email", { required: true })} /> },
 *     // required 를 직접 지정하면 그게 우선한다 (content 에 required prop 이 없는 경우 등).
 *     { label: "상태", content: <select>...</select>, required: true },
 *   ]}
 * />
 *
 * @example
 * // 상세 조회 로딩 중 — 레이아웃(높이) 유지한 채 흐리게 + 스피너
 * <AdminDescriptions title="회원 정보" items={items} loading={fetch.isLoading} />
 *
 * @example
 * // items 대신 임의의 콘텐츠(AdminTable 등)를 title + 카드 테두리 안에 넣고 싶을 때
 * <AdminDescriptions title="상품 정보">
 *   <AdminTable columns={columns} data={rows} />
 * </AdminDescriptions>
 */
import { Fragment, isValidElement, type ReactNode } from "react";
import { Loader2 } from "lucide-react";

import { cn } from "../../lib/utils";

/** content 로 넘긴 엘리먼트 자체가 required=true 를 갖고 있는지 (AdminFormField/AdminInput/AdminTextarea 등) */
function isRequiredContent(content: ReactNode): boolean {
  return (
    isValidElement(content) &&
    (content.props as { required?: boolean }).required === true
  );
}

export interface AdminDescriptionItem {
  label: ReactNode;
  content: ReactNode;
  /** 차지할 열 수 (columns 까지). 기본 1 */
  span?: number;
  /**
   * 라벨 옆에 빨간 * 표시 (입력 필드가 필수일 때).
   * 지정하지 않으면 content 엘리먼트의 required prop(true)을 자동으로 반영한다.
   */
  required?: boolean;
}

export interface AdminDescriptionsProps {
  /** 라벨/내용 목록. children을 대신 쓸 경우 생략 가능 */
  items?: AdminDescriptionItem[];
  /** 섹션 제목 (선택) */
  title?: ReactNode;
  /**
   * items 대신 임의의 콘텐츠(AdminTable 등)를 넣고 싶을 때 사용.
   * 지정하면 items는 무시되고, title + 카드 테두리 안에 children을 그대로 보여준다.
   */
  children?: ReactNode;
  /**
   * 배치 방식 (기본 "row")
   * - "row": 라벨(왼쪽) + 내용(오른쪽), 테두리 표 형태
   * - "stacked": 작은 회색 라벨(위) + 내용(아래) 형태
   */
  layout?: "row" | "stacked";
  /** 한 줄에 표시할 항목 수 (기본 1) */
  columns?: number;
  /** 테두리형 (row 레이아웃에서만, 기본 true). false 면 구분선만 */
  bordered?: boolean;
  /** 라벨 셀 최소 너비 (row 레이아웃, 기본 7rem) */
  labelWidth?: string;
  className?: string;
  /**
   * true 면 내용을 흐리게(opacity) 하고 가운데에 스피너를 겹쳐 보여준다.
   * 레이아웃 높이가 유지되므로 상세 조회(fetch.isLoading) 중 화면이 흔들리지 않는다.
   */
  loading?: boolean;
}

export function AdminDescriptions({
  items = [],
  title,
  layout = "row",
  columns = 1,
  bordered = true,
  labelWidth = "7rem",
  className,
  loading = false,
  children,
}: AdminDescriptionsProps) {
  const titleEl = title ? (
    <div className="mb-4 text-sm font-semibold text-foreground">{title}</div>
  ) : null;

  let body: ReactNode;

  // children: items 표 렌더링 대신, title + 카드 테두리 안에 그대로 보여준다.
  if (children) {
    body = (
      <div
        className={cn(
          bordered && "rounded-lg border bg-card px-6 py-5",
          className
        )}
      >
        {titleEl}
        {children}
      </div>
    );
  } else if (layout === "stacked") {
    body = (
      <div
        className={cn(
          bordered && "rounded-lg border bg-card px-6 py-5",
          className
        )}
      >
        {titleEl}
        <div
          className="grid gap-x-6 gap-y-4"
          style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
        >
          {items.map((item, i) => {
            const span = Math.min(item.span ?? 1, columns);
            return (
              <div
                key={i}
                style={span > 1 ? { gridColumn: `span ${span}` } : undefined}
              >
                <div className="text-xs text-muted-foreground">
                  {item.label}
                  {(item.required ?? isRequiredContent(item.content)) && (
                    <span className="ml-0.5 text-destructive">*</span>
                  )}
                </div>
                <div className="mt-1 text-sm text-foreground">
                  {item.content}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  } else {
    // row: 라벨(왼쪽) + 내용(오른쪽) 표. 항목을 columns 기준으로 행에 채운다.
    // CSS 그리드 auto-flow 대신 직접 행을 계산해야 span/순서가 정확하다. (테이블)
    type Packed = AdminDescriptionItem & { span: number };
    const rows: Packed[][] = [];
    let cur: Packed[] = [];
    let used = 0;
    for (const item of items) {
      const span = Math.min(Math.max(item.span ?? 1, 1), columns);
      // 남은 칸보다 크면 현재 행을 마무리(마지막 항목이 남은 칸 채움)하고 새 행 시작
      if (used + span > columns) {
        if (cur.length) cur[cur.length - 1].span += columns - used;
        rows.push(cur);
        cur = [];
        used = 0;
      }
      cur.push({ ...item, span });
      used += span;
      if (used === columns) {
        rows.push(cur);
        cur = [];
        used = 0;
      }
    }
    if (cur.length) {
      cur[cur.length - 1].span += columns - used; // 마지막 행 남은 칸 채움
      rows.push(cur);
    }

    const cellBorder = bordered ? "border border-border" : "";

    body = (
      <div className={className}>
        {titleEl}
        <div
          className={cn(
            "overflow-hidden rounded-lg text-sm",
            !bordered && "border-t"
          )}
        >
          <table className="w-full border-collapse">
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri}>
                  {row.map((item, ci) => (
                    <Fragment key={ci}>
                      <th
                        scope="row"
                        style={{ width: labelWidth }}
                        className={cn(
                          "px-3 py-2 text-left align-top font-medium text-muted-foreground",
                          bordered ? "bg-muted" : "border-b",
                          cellBorder
                        )}
                      >
                        {item.label}
                        {(item.required ?? isRequiredContent(item.content)) && (
                          <span className="ml-0.5 text-destructive">*</span>
                        )}
                      </th>
                      <td
                        colSpan={item.span * 2 - 1}
                        className={cn(
                          "px-3 py-2 align-top text-foreground",
                          bordered ? "bg-card" : "border-b",
                          cellBorder
                        )}
                      >
                        {item.content}
                      </td>
                    </Fragment>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (!loading) return body;

  return (
    <div className="relative">
      <div className="pointer-events-none opacity-50">{body}</div>
      <div className="absolute inset-0 flex items-center justify-center">
        <Loader2 className="size-5 animate-spin text-muted-foreground" />
      </div>
    </div>
  );
}
